import { useDebounce, useI18N } from "@sundae/react-hooks";
import {
  Balance,
  Button,
  Cross1Icon,
  Dialog,
  Input,
  Loader,
  MagnifyingGlassIcon,
  ScrollArea,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import { FC, useCallback, useMemo, useState } from "react";

import { SundaeUtils } from "@sundaeswap/core/utilities";
import isEqual from "lodash/isEqual";
import InfiniteScroll from "react-infinite-scroll-component";
import { useCustomCompareMemo } from "use-custom-compare";
import { INPUT_DEBOUNCE_IN_MS, NUMBER_TABLE_ITEMS } from "../../../constants/page.constants";
import { testIds } from "../../../constants/testIds.constants";
import {
  usePoolsBySearch,
  usePopularPools,
  useRawPoolsByAssetIds,
} from "../../../gql/hooks/pools.query";
import { useAdaData } from "../../../hooks/useAdaData";
import { Actions, useAppContext, useProfileAppContext } from "../../../stores";
import { calculateTVLOfPoolInADA, getUniqueListOfPools } from "../../../utils/pool.utils";
import NoDataIndicator from "../../NoDataIndicator";
import PoolSelectItem from "../../PoolSelectItem";
import PoolSelectItemSkeleton from "../../PoolSelectItem/PoolSelectItem.skeleton";

const PoolSelectDialog: FC = () => {
  const { t, currentLocale } = useI18N("liquidity");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    state: { activeZapAsset, activeZapPool, dialogs },
    dispatch,
  } = useAppContext();
  const [renderedPools, setRenderedPools] = useState(NUMBER_TABLE_ITEMS);
  const [searchInput, setSearchInput] = useState("");

  const { data: adaData, isFetching: isFetchingAdaData } = useAdaData();
  const { data: popularPoolsData, isFetching: isFetchingPopularPools } = usePopularPools();
  const debouncedSearchValue = useDebounce(searchInput, { wait: INPUT_DEBOUNCE_IN_MS });
  const { data: poolsSearched, isFetching: searchingPools } =
    usePoolsBySearch(debouncedSearchValue);

  const { data: poolsByAssetsIdData } = useRawPoolsByAssetIds({
    assetIds: [activeZapAsset?.metadata.assetId ?? ""],
    enabled: !!activeZapAsset?.metadata.assetId || SundaeUtils.isAdaAsset(activeZapAsset?.metadata),
  });

  const poolsByAssetsId = poolsByAssetsIdData?.stats ?? [];

  const searchedPools = useMemo(() => {
    const list = poolsSearched ? poolsSearched.stats : popularPoolsData.stats;
    const pools = getUniqueListOfPools([...list, ...poolsByAssetsId]);
    const isAdaAsset = SundaeUtils.isAdaAsset(activeZapAsset?.metadata);
    const assetsIdsToCompare = isAdaAsset
      ? SundaeUtils.ADA_ASSET_IDS
      : [activeZapAsset?.metadata.assetId ?? ""];
    return activeZapAsset
      ? pools.filter(({ assetA, assetB }) =>
          [assetA.assetId, assetB.assetId].some((assetId) => assetsIdsToCompare.includes(assetId))
        )
      : [];
  }, [poolsSearched, popularPoolsData, activeZapAsset, poolsByAssetsId]);

  const isLoading = isFetchingPopularPools || searchingPools || isFetchingAdaData;

  const handleCloseDialog = useCallback(() => {
    Actions.setDialogState({ dialog: "liquidity.zapPoolSelect", isOpen: false }, dispatch);
  }, [dispatch]);

  /**
   * Defines the content that will be rendered.
   */
  const renderedContent = useCustomCompareMemo(
    () => {
      // sort so the activeZapPool is always on top
      const sortedPools = searchedPools?.sort((a, b) => {
        if (a.ident === activeZapPool?.ident) return -1;
        if (b.ident === activeZapPool?.ident) return 1;
        return 0;
      });

      if (isLoading) {
        return Array.from({ length: 3 }).map((item, index) => (
          <PoolSelectItemSkeleton key={`${item}-${index}}`} />
        ));
      }

      if (sortedPools?.length)
        return (
          <div className="w-full">
            <InfiniteScroll
              className="flex max-h-[20rem] w-full flex-col gap-2 xxl:max-h-[28rem]"
              dataLength={renderedPools}
              hasMore
              height={504}
              loader={null}
              next={() => {
                setRenderedPools((prev) => prev + NUMBER_TABLE_ITEMS);
              }}
            >
              {sortedPools.slice(0, renderedPools).map((pool) => {
                const { ident } = pool;
                const isActivePool = ident === activeZapPool?.ident;
                const tvl = calculateTVLOfPoolInADA(pool, adaData?.usd ?? 0);

                if (!pool) return null;

                return (
                  <PoolSelectItem
                    balance={
                      <Balance abbreviate={false} locale={currentLocale} size="sm" value={tvl} />
                    }
                    balanceLabel={t("tvl", { ns: "glossary" })}
                    isSelected={isActivePool}
                    key={ident}
                    onClick={() => {
                      if (!isActivePool) {
                        Actions.setActiveZapPool(pool, dispatch);
                        handleCloseDialog();
                      }
                    }}
                    pool={pool}
                  />
                );
              })}
            </InfiniteScroll>
          </div>
        );

      return (
        <div className="flex flex-col items-center justify-center gap-4">
          <NoDataIndicator context="pools" />
          <Button
            onClick={() => {
              Actions.setDialogState({ dialog: "liquidity.zap", isOpen: false }, dispatch);
              Actions.setDialogState({ dialog: "liquidity.createPool", isOpen: true }, dispatch);
            }}
            variant="gradient"
            className="min-w-[200px]"
          >
            {t("liquidity_create")}
          </Button>
        </div>
      );
    },
    [
      isLoading,
      searchedPools,
      activeZapPool,
      currentLocale,
      handleCloseDialog,
      privacyEnabled,
      renderedPools,
    ],
    isEqual
  );

  return (
    <Dialog
      closeDialog={handleCloseDialog}
      description={t("zap.dialogCopy")}
      open={dialogs.liquidity.zapPoolSelect.isOpen}
      title={t("zap.selectPool")}
      isDialogScrollable={false}
      testId={testIds.poolSelectDialog.content}
    >
      <div className="relative w-full max-w-lg flex-1">
        <Input
          onChange={(e) => setSearchInput(e.target.value)}
          size="sm"
          placeholder={`${t("zap.selectPool")}...`}
          startAdornment={<MagnifyingGlassIcon width={20} height={20} />}
          value={searchInput}
          {...(searchInput &&
            !searchingPools && {
              endAdornment: (
                <Cross1Icon className="h-3 w-3 cursor-pointer" onClick={() => setSearchInput("")} />
              ),
            })}
        />
        <Loader
          size="xsmall"
          className={cx("!absolute right-6 top-1/2 -translate-y-1/2", {
            hidden: !searchingPools,
          })}
        />
      </div>

      <ScrollArea className="w-full">
        <div className="max-h-[20rem] xl:max-h-[28rem]">
          <div
            className={cx("flex min-h-[15rem] w-full flex-col items-center gap-2", {
              "justify-start": !isLoading,
            })}
          >
            {renderedContent}
          </div>
        </div>
      </ScrollArea>
    </Dialog>
  );
};

export default PoolSelectDialog;
